import { useEffect, useRef } from 'react'
import { Badge, Button, Spinner } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { BadgeVariant } from '@/components/ui'
import type { Notification } from '@/types'
import { formatDistanceToNow, format, parseISO } from 'date-fns'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/cn'
import { ExternalLink } from 'lucide-react'
import {
  resolveNotificationInternalPath,
} from '../utils/notificationNavigation'
import { isShiftAlertNotification } from '../utils/shiftAlertNotificationContext'
import { formatNotificationType } from '../utils/notificationTaxonomy'
import { ShiftAlertNotificationRow } from './ShiftAlertNotificationRow'
import { usePermissions } from '@/hooks/usePermissions'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { ChatNotificationContent } from './ChatNotificationContent'
import { getChatNotificationContext } from '../utils/chatNotificationContext'

export interface NotificationListProps {
  notifications: Notification[];
  total: number;
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  /** Re-runs the query behind this list from the error state's retry button. */
  onRetry?: () => Promise<unknown> | void;
  /** True while more notifications remain to load on scroll. */
  hasMore: boolean;
  /** True while the next batch is loading. */
  isLoadingMore: boolean;
  /** Called when the scroll sentinel enters the viewport. */
  onLoadMore: () => void;
  onMarkAsRead: (id: string, silent?: boolean) => void;
  onClearRead: () => void;
  isClearingRead: boolean;
  /** Shown when the list is empty (e.g. tailored to the active filters). */
  emptyLabel?: string;
}

function formatTime(iso: string): string {
  try {
    const date = parseISO(iso);
    const now = new Date();
    const diffDays = (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays < 1) return formatDistanceToNow(date, { addSuffix: true });
    return format(date, 'PPp');
  } catch {
    return iso;
  }
}

function priorityBadgeVariant(priority: string): BadgeVariant {
  switch (priority) {
    case 'urgent':
      return 'danger';
    case 'high':
      return 'warning';
    case 'low':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function NotificationList({
  notifications,
  total,
  unreadCount,
  isLoading,
  error,
  onRetry,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onMarkAsRead,
  onClearRead,
  isClearingRead,
  emptyLabel = 'No notifications.',
}: NotificationListProps): React.ReactElement {
  const safeList = Array.isArray(notifications) ? notifications : [];
  const { can } = usePermissions();

  // Auto-load the next batch when the sentinel scrolls into view. `rootMargin`
  // starts the fetch a little before the sentinel is visible so scrolling stays
  // smooth. onLoadMore no-ops when there's nothing more or a load is in flight.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { rootMargin: '240px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, safeList.length]);

  if (isLoading) {
    return (
      <SectionLoader variant="list" rows={6} label="Loading notifications" />
    )
  }

  if (error) {
    return <QueryErrorState error={error} title="Couldn't load notifications" onRetry={onRetry} />;
  }

  if (safeList.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 py-12 text-center text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {unreadCount > 0 && (
            <span className="text-xs font-medium text-primary">{unreadCount} unread</span>
          )}
          {total > 0 && (
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Showing {safeList.length} of {total}
            </p>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={onClearRead}
          disabled={total === 0 || unreadCount === total}
          loading={isClearingRead}
        >
          <span>Clear read</span>
        </Button>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Title
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Date
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {safeList.map((n) =>
              isShiftAlertNotification(n) ? (
                <ShiftAlertNotificationRow
                  key={n._id}
                  notification={n}
                  onMarkAsRead={onMarkAsRead}
                />
              ) : (
                <tr
                  key={n._id}
                  className={cn(
                    'text-sm transition-colors',
                    n.isRead
                      ? 'text-gray-600 dark:text-gray-400'
                      : 'bg-primary/5 font-medium text-gray-900 dark:bg-primary/10 dark:text-gray-100'
                  )}
                >
                  <td className="px-4 py-3">
                    {getChatNotificationContext(n) ? (
                      <ChatNotificationContent notification={n} compact />
                    ) : (
                      <div>
                        <span className="block">{n.title}</span>
                        {n.message && (
                          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                            {n.message}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <Badge variant={priorityBadgeVariant(n.priority)}>
                      {formatNotificationType(n.type)}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-600 dark:text-gray-400">
                    {n.createdAt ? formatTime(n.createdAt) : '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {!n.isRead && (
                        <Button type="button" variant="ghost" onClick={() => onMarkAsRead(n._id)}>
                          Mark read
                        </Button>
                      )}
                      {(() => {
                        const internalPath = resolveNotificationInternalPath(n, { can });
                        if (internalPath) {
                          return (
                            <Link
                              to={internalPath}
                              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                              onClick={() => {
                                if (!n.isRead) onMarkAsRead(n._id, true);
                              }}
                            >
                              View
                              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                            </Link>
                          );
                        }
                        if (n.reference?.url) {
                          return (
                            <a
                              href={n.reference.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                              onClick={() => {
                                if (!n.isRead) onMarkAsRead(n._id, true);
                              }}
                            >
                              View
                              <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                            </a>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Infinite-scroll sentinel: loads more as it nears the viewport. */}
      {hasMore && (
        <div
          ref={sentinelRef}
          className="flex items-center justify-center py-4 text-xs text-gray-500 dark:text-gray-400"
        >
          {isLoadingMore ? (
            <span className="inline-flex items-center gap-2">
              <Spinner size="sm" />
              Loading more…
            </span>
          ) : (
            <span>Scroll to load more</span>
          )}
        </div>
      )}
      {!hasMore && safeList.length > 0 && (
        <p className="py-3 text-center text-xs text-gray-400 dark:text-gray-500">
          You're all caught up.
        </p>
      )}
    </div>
  );
}

NotificationList.displayName = 'NotificationList';
